import { ChevronRight, ExpandLess, Send } from '@mui/icons-material'
import {
  Avatar,
  Box,
  BoxProps,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import AvatarWithLoader from 'components/avatar-with-loader'
import moment from 'moment'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { Comment } from 'types/comment'
import {
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  incidentReport: IncidentReportPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    incidentReport: IncidentReportOperations
    user: UserOperations
  }
}

interface OwnProps {
  comments: Comment[]
  submitFnCallback: (comment: string) => void
  containerProps?: BoxProps
  maxCommentHeight?: number
}

type AppProps = StateProps & DispatchProps & OwnProps

const NivosCommentReplyFC: React.FC<{ comment: Comment }> = props => {
  return (
    <Box marginLeft={2} marginY={3} display={'flex'} alignItems={'center'}>
      <Box height={30} width={30} sx={{ cursor: 'pointer' }} marginRight={1}>
        <AvatarWithLoader
          src={props.comment.createdBy.image?.url ?? ''}
          avatarProps={{ sx: { height: 30, width: 30 } }}
        />
      </Box>
      <Box display={'flex'} flexDirection={'column'} flexGrow={1}>
        <Box display={'flex'} alignItems={'center'}>
          <Typography fontSize={14} marginRight={2}>
            <strong>{props.comment.createdBy.fullName}</strong>
          </Typography>
          <Box flexGrow={1} />
          <Typography fontSize={10} color={'#d4d2d2'}>
            {moment(props.comment.createdAt).fromNow()}
          </Typography>
        </Box>
        <Typography fontSize={12}>{props.comment.comment}</Typography>
      </Box>
    </Box>
  )
}

const NivosCommentFC: React.FC<{ comment: Comment }> = props => {
  const { comment } = props
  const [localComment, setLocalComment] = React.useState(comment)
  const [reply, setReply] = React.useState('')
  const [showReplies, setShowReplies] = React.useState(false)

  const addReply = (): void => {
    void post(comment.replyUrl, { comment: reply }).then(res => {
      const newReply: Comment = objectifyDateStringFields(res)
      const newReplies = localComment.replies
      newReplies.push(newReply)
      setLocalComment({ ...localComment, replies: newReplies })
      setShowReplies(true)
    })
  }

  return (
    <Box
      padding={2}
      height={'fit-content'}
      display={'flex'}
      flexDirection={'column'}
    >
      <Box display={'flex'} alignItems={'center'} paddingBottom={1}>
        <Avatar
          sx={{ width: '45px', height: '45px', marginRight: '15px' }}
          src={
            localComment.createdBy.image !== undefined
              ? localComment.createdBy.image.url
              : ''
          }
        />
        <Box display={'flex'} alignItems={'center'} flexGrow={1}>
          <Typography fontSize={24} marginRight={2}>
            {localComment.createdBy.fullName}
          </Typography>
          <Typography fontSize={14} color={'#d4d2d2'}>
            {localComment.createdAt.toUTCString().slice(0, 16)}
          </Typography>
        </Box>
        <Typography fontSize={14} color={'#d4d2d2'}>
          {moment(localComment.createdAt).fromNow()}
        </Typography>
      </Box>
      <Typography gutterBottom paddingLeft={'60px'} fontSize={16}>
        {localComment.comment}
      </Typography>
      <Box paddingX={5}>
        <Collapse in={showReplies}>
          {localComment.replies.map((reply, index) => (
            <NivosCommentReplyFC key={index} comment={reply} />
          ))}
        </Collapse>
      </Box>
      <Box
        color={'#d4d2d2'}
        marginY={1}
        marginLeft={3}
        display={'flex'}
        alignItems={'center'}
      >
        {localComment.replies.length > 0 && (
          <IconButton onClick={() => setShowReplies(!showReplies)}>
            {showReplies && <ExpandLess fontSize={'small'} />}
            {!showReplies && <ChevronRight fontSize={'small'} />}
          </IconButton>
        )}
        {localComment.replies.length === 0 && <Box width={'36px'} />}
        <InputBase
          fullWidth
          placeholder={'Reply'}
          value={reply}
          onChange={event => {
            if (!showReplies) setShowReplies(true)
            setReply(event.currentTarget.value)
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              addReply()
              setReply('')
              event.preventDefault()
            }
          }}
          sx={{ fontSize: 12 }}
        />
      </Box>
      <Box width={0.95} paddingLeft={'40px'}>
        <Divider variant={'middle'} />
      </Box>
    </Box>
  )
}

const NivosCommentSectionFC: React.FC<any> = (props: AppProps) => {
  const { comments, submitFnCallback, containerProps, maxCommentHeight, user } =
    props
  const [comment, setComment] = React.useState('')
  const bottomCommentRef = React.useRef<HTMLDivElement>()

  const submit = (): void => {
    submitFnCallback(comment)
  }

  React.useEffect(() => {
    if (bottomCommentRef.current !== null) {
      const target: any = bottomCommentRef.current
      target.parentNode.scrollTop = target.offsetTop
    }
  }, [comments])

  return (
    <Box {...containerProps}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        maxHeight={
          maxCommentHeight !== undefined ? maxCommentHeight : 'initial'
        }
        sx={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        {comments.map((comment, index) => (
          <NivosCommentFC key={index} comment={comment} />
        ))}
        {comments.length < 1 && (
          <Box display={'flex'} justifyContent={'center'} width={1}>
            <Typography variant={'h6'} fontStyle={'italic'}>
              No comments
            </Typography>
          </Box>
        )}
        <Box ref={bottomCommentRef} />
      </Box>
      <Box padding={2} />
      <Box display={'flex'} flexDirection={'column'}>
        <Box height={'fit-content'} display={'flex'} alignItems={'center'}>
          <Avatar
            sx={{ width: '50px', height: '50px', marginRight: '15px' }}
            src={user.image?.url}
          />
          <TextField
            fullWidth
            multiline
            value={comment}
            placeholder={'Write a new comment'}
            maxRows={3}
            size={'small'}
            onChange={event => setComment(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                submit()
                setComment('')
                event.preventDefault()
              }
            }}
          />
          <Box height={'fit-content'}>
            <IconButton
              onClick={() => {
                submit()
                setComment('')
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    incidentReport: state.incidentReport,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  incidentReport: incidentReportOperations,
  user: userOperations
})

const NivosCommentSection = connect(
  mapStateToProps,
  mapDispatchToProps
)(NivosCommentSectionFC) as React.FC<OwnProps>

export default NivosCommentSection
